/**
 * Autobuild — the pipeline's nested `autobuild` map on a `bug_reports` doc.
 *
 * Theo's bug-report / feature-request module keeps its own `status` field
 * (new → reviewed → in_progress → done / wont_fix) meaningful to humans. The
 * unattended build pipeline (.github/workflows/autobuild.yml) records its
 * machine detail in one nested map on the same doc, `autobuild`, and nothing
 * else on the doc changes shape. Three writers touch that map:
 *
 *   - the pipeline itself (scripts/autobuild/*.mjs, Admin SDK) — phase, log,
 *     links, evidence, summaries;
 *   - the app (/home/bugs and the Plate) — the reporter's answer to a
 *     question, a director's Approve / Request changes decision (via the
 *     owner-verified `autobuildDecide` callable), a director's Redirect
 *     (`autobuildRedirect`), and anyone's post in the thread
 *     (`autobuildThreadPost`);
 *   - the Claude connector (functions/src/mcp) — the same things from chat;
 *   - Slack (#theo-builds) — the buttons, and replies in the mirrored thread.
 *
 * Everything that decides what one of those writes LOOKS like lives here, so a
 * decision made in Theo, in Claude, or by the pipeline reads identically to
 * the scheduler that acts on it (functions/src/autobuild/dispatcher.ts).
 *
 * Phase → app status is mirrored in autobuild.config.json (`requests.statuses`)
 * for the .mjs scripts; a test pins the two together.
 */

import type { BugReportStatus } from './bug-reports';

export const AUTOBUILD_PHASES = [
  'lodged',
  'queued',
  'understanding',
  'needsInfo',
  'answered',
  'building',
  'testing',
  'awaitingApproval',
  'awaitingReuben',
  'redirected',
  'shipping',
  'blocked',
  'rolledBack',
  'shipped',
  'alreadyDone',
  'declined',
] as const;

export type AutobuildPhase = (typeof AUTOBUILD_PHASES)[number];

/** The app status each pipeline phase lands the report on (requests.statuses in the config). */
export const AUTOBUILD_APP_STATUS: Record<AutobuildPhase, BugReportStatus> = {
  lodged: 'new',
  queued: 'new',
  understanding: 'reviewed',
  needsInfo: 'reviewed',
  answered: 'new',
  building: 'in_progress',
  testing: 'in_progress',
  awaitingApproval: 'in_progress',
  awaitingReuben: 'in_progress',
  redirected: 'reviewed',
  shipping: 'in_progress',
  blocked: 'in_progress',
  rolledBack: 'in_progress',
  shipped: 'done',
  alreadyDone: 'done',
  declined: 'wont_fix',
};

/**
 * The identity the pipeline writes as. A synthetic id (never a `users` doc)
 * so bells say "Theo Autobuild fixed your report" and Plate tasks it raises
 * are not assigned to a real person. autobuild.config.json → notify.actorId
 * / actorName mirror this for the .mjs scripts.
 */
export const AUTOBUILD_ACTOR = { id: 'autobuild', name: 'Theo Autobuild' } as const;

export type AutobuildVerdict = 'approve' | 'changes';

export const AUTOBUILD_VERDICTS: readonly AutobuildVerdict[] = ['approve', 'changes'];

/** Where a decision or an answer was made. */
export type AutobuildSource = 'theo' | 'plate' | 'connector' | 'pipeline' | 'slack';

export interface AutobuildLogEntry {
  at: string;
  phase: string;
  text: string;
  runUrl?: string | null;
  /** Display name of whoever wrote the entry when it wasn't the pipeline. */
  by?: string;
}

export interface AutobuildEvidence {
  /** e.g. "Before · mobile", "After · desktop" */
  label: string;
  url: string;
  /** ISO — signed URLs expire; the UI hides expired ones. */
  expiresAt: string;
  kind?: 'before' | 'after' | 'other' | 'video';
  project?: 'mobile' | 'desktop' | string;
  /** Storage object path, for re-signing. */
  path?: string;
}

export interface AutobuildDecision {
  verdict: AutobuildVerdict;
  /** Required for 'changes' (what to change), optional for 'approve'. */
  note: string;
  at: string;
  byId: string;
  byName: string;
  via: AutobuildSource;
}

/**
 * A director's Redirect — "you're building the wrong thing". Server-written
 * only (the `autobuildRedirect` callable, the connector's `redirect_build`,
 * the Slack button). The note overrides the original request, any earlier
 * SPEC, answer or decision: the pipeline stops the run in flight, keeps the
 * branch and the PR, and starts again from a fresh understand phase whose
 * spec begins with this note. Request changes stays for small fixes after
 * approval; Redirect is for any phase from understanding to awaiting approval
 * (and a blocked build).
 */
export interface AutobuildRedirect {
  note: string;
  at: string;
  byId: string;
  byName: string;
  via: AutobuildSource;
  /** The phase the build was in when it was redirected. */
  phaseBefore?: string | null;
  /** The run that was in flight (autobuild.runUrl at the time), if any. */
  stoppedRunUrl?: string | null;
  /** Stamped by the scheduler once it has tried to stop that run. */
  cancelledAt?: string | null;
  cancelOutcome?: string | null;
}

/** Where a thread post came from. */
export type AutobuildThreadKind =
  | 'post' // a person wrote in the thread
  | 'phase' // a pipeline phase reporting in: what it understood, what it is about to do, a question
  | 'question' // the pipeline's needs-info question
  | 'answer' // the answer to it
  | 'decision' // Approve / Request changes
  | 'redirect'; // a Redirect

/**
 * One post in the report's Autobuild thread (`autobuild.thread`). The thread
 * is the conversation between the people on the report and the pipeline:
 * anyone with access can post; a tenant owner's post counts as an
 * instruction (owner-ness is decided at read time from tenants/{id}.ownerIds,
 * never stored — so nobody can write themselves a director badge). Every
 * phase reads the whole thread before it starts and replies in it. Posts are
 * server-written (the `autobuildThreadPost` callable, the connector, the
 * pipeline's write-back, the Slack events bridge).
 */
export interface AutobuildThreadPost {
  /** Unique within the thread — lets the Slack bridge and the UI dedupe. */
  id: string;
  at: string;
  byId: string;
  byName: string;
  text: string;
  kind: AutobuildThreadKind;
  via: AutobuildSource;
  /** For pipeline posts: which phase wrote it. */
  phase?: string | null;
  runUrl?: string | null;
  /** The Slack message this post is mirrored to / came from. */
  slackTs?: string | null;
}

/** The #theo-builds message the report's thread is mirrored under. */
export interface AutobuildSlackThread {
  channel: string;
  ts: string;
}

/**
 * The Brief — four short boxes for the approver, written by the audit phase
 * at the end of a build and copied onto the report (autobuild.brief). It is
 * the first thing on the dashboard detail and the body of the Slack approval
 * ask; the full reports stay in their tabs.
 *
 *   asked    — what they asked for, in their words, one line
 *   means    — what that actually involved: which screens, which behaviour
 *   did      — what was built and tested, one line
 *   yourCall — anything the approver must decide or know first; empty = nothing
 */
export interface AutobuildBrief {
  asked: string;
  means: string;
  did: string;
  yourCall: string[];
  at: string;
  runUrl?: string | null;
}

export const AUTOBUILD_BRIEF_BOX_MAX_CHARS = 600;
export const AUTOBUILD_BRIEF_MAX_CALLS = 10;

const briefLine = (v: unknown): string =>
  typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, AUTOBUILD_BRIEF_BOX_MAX_CHARS) : '';

/**
 * Tolerant read of a brief as the auditor wrote it (`your_call` or `yourCall`,
 * a string or a list). null when none of the three boxes has text.
 */
export function readAutobuildBrief(raw: unknown, at?: string, runUrl?: string | null): AutobuildBrief | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const asked = briefLine(o.asked);
  const means = briefLine(o.means);
  const did = briefLine(o.did);
  if (!asked && !means && !did) return null;
  const callsRaw = o.yourCall ?? o.your_call;
  const calls = (Array.isArray(callsRaw) ? callsRaw : typeof callsRaw === 'string' ? [callsRaw] : [])
    .map(briefLine)
    .filter((c) => c && !/^(nothing|none|n\/a)\.?$/i.test(c))
    .slice(0, AUTOBUILD_BRIEF_MAX_CALLS);
  return {
    asked,
    means,
    did,
    yourCall: calls,
    at: typeof o.at === 'string' && o.at ? o.at : at ?? new Date().toISOString(),
    runUrl: runUrl !== undefined ? runUrl : typeof o.runUrl === 'string' ? o.runUrl : null,
  };
}

/**
 * One phase's paperwork, copied onto the report by the pipeline so the
 * dashboard (/home/admin/autobuild) never has to open GitHub: the text of
 * SPEC.md / PR.md / TEST-REPORT.md / REGRESSION-REPORT.md and the auditor's
 * verdict. Capped at AUTOBUILD_DOC_MAX_CHARS — the full files stay on the
 * branch under docs/autobuild/requests/<id>/.
 */
export interface AutobuildDoc {
  text: string;
  at: string;
  runUrl?: string | null;
  /** True when the stored text is the head of a longer file. */
  truncated?: boolean;
}

export interface AutobuildAuditFinding {
  severity: string;
  where?: string;
  what: string;
  why?: string;
  how?: string;
}

export interface AutobuildDocs {
  spec?: AutobuildDoc | null;
  /** PR.md — what was built, written by the implementer, refreshed after the audit. */
  pr?: AutobuildDoc | null;
  changedFiles?: string[];
  test?: AutobuildDoc | null;
  testResult?: string | null;
  regression?: AutobuildDoc | null;
  regressionResult?: string | null;
  audit?: AutobuildDoc | null;
  auditResult?: string | null;
  auditFindings?: AutobuildAuditFinding[];
  /** WALKTHROUGH.md — the live walk-through's report (a fresh session using the preview like a person). */
  walkthrough?: AutobuildDoc | null;
  /** running | pass | fail | skipped — the walk-through runs beside the approval ask, never instead of it. */
  walkthroughResult?: string | null;
  /** The preview channel the walk-through used — Reuben can open it himself while it lasts. */
  walkthroughPreviewUrl?: string | null;
}

/** Stamped by every pipeline status write: the drain switch as the run saw it. */
export interface AutobuildPipelineStamp {
  drain: boolean;
  at: string;
  runUrl?: string | null;
}

export interface AutobuildState {
  phase?: AutobuildPhase | string;
  /** When `phase` was last changed — "how long it has been there" on the dashboard. */
  phaseAt?: string | null;
  log?: AutobuildLogEntry[];
  docs?: AutobuildDocs;
  pipeline?: AutobuildPipelineStamp | null;
  /** The auditor's four-box Brief for the approver (see AutobuildBrief). */
  brief?: AutobuildBrief | null;
  /**
   * The protected areas the change touches (policy.riskyPaths matches),
   * stamped when the build parks for approval. Non-empty = Approve asks for
   * a "this touches rules/auth" confirmation first.
   */
  riskReasons?: string[];
  /** The one open question the pipeline asked (needsInfo). */
  question?: string | null;
  questionAt?: string | null;
  answer?: string | null;
  answeredAt?: string | null;
  answeredBy?: string | null;
  answeredByName?: string | null;
  prUrl?: string | null;
  prNumber?: number | null;
  runUrl?: string | null;
  /** Plain English for the reporter, set when the report is done. */
  summary?: string | null;
  /** The three-line summary for the approver (awaitingApproval). */
  reviewSummary?: string | null;
  evidence?: AutobuildEvidence[];
  approvalRequestedAt?: string | null;
  decision?: AutobuildDecision | null;
  /** Stamped by the scheduler when it acted on `decision`. */
  decisionDispatchedAt?: string | null;
  /** A director's Redirect (see AutobuildRedirect); the newest one wins. */
  redirect?: AutobuildRedirect | null;
  /** Stamped by the scheduler when it acted on `redirect`. */
  redirectDispatchedAt?: string | null;
  /** One line from the understand phase: what the build is about to do. On every approval and needs-info message. */
  scope?: string | null;
  /** The conversation on the report — people and phases (see AutobuildThreadPost). */
  thread?: AutobuildThreadPost[];
  /** The #theo-builds message the thread is mirrored under, once the pipeline has posted it. */
  slack?: AutobuildSlackThread | null;
  /** Stamped by the scheduler on every repository_dispatch it sends. */
  dispatchedAt?: string | null;
  dispatchCount?: number;
  pickedAt?: string | null;
  pickedRun?: string | null;
  lastNote?: string | null;
  updatedAt?: string | null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** Tolerant read of a stored map — the pipeline writes it, humans never do. */
export function readAutobuild(raw: unknown): AutobuildState {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const log = Array.isArray(o.log)
    ? (o.log.filter((e) => e && typeof e === 'object') as AutobuildLogEntry[])
    : [];
  const evidence = Array.isArray(o.evidence)
    ? (o.evidence.filter(
        (e) => e && typeof e === 'object' && typeof (e as AutobuildEvidence).url === 'string',
      ) as AutobuildEvidence[])
    : [];
  const d = o.decision && typeof o.decision === 'object' ? (o.decision as AutobuildDecision) : null;
  const docs = o.docs && typeof o.docs === 'object' ? (o.docs as AutobuildDocs) : {};
  const pipeline =
    o.pipeline && typeof o.pipeline === 'object' && typeof (o.pipeline as AutobuildPipelineStamp).at === 'string'
      ? ({ ...(o.pipeline as AutobuildPipelineStamp), drain: (o.pipeline as AutobuildPipelineStamp).drain === true })
      : null;
  const riskReasons = Array.isArray(o.riskReasons)
    ? o.riskReasons.filter((r): r is string => typeof r === 'string' && !!r.trim()).map((r) => r.trim())
    : [];
  const thread = Array.isArray(o.thread)
    ? (o.thread.filter(
        (p) => p && typeof p === 'object' && typeof (p as AutobuildThreadPost).text === 'string' && typeof (p as AutobuildThreadPost).at === 'string',
      ) as AutobuildThreadPost[])
    : [];
  const redirect =
    o.redirect && typeof o.redirect === 'object' && typeof (o.redirect as AutobuildRedirect).note === 'string' && (o.redirect as AutobuildRedirect).note
      ? (o.redirect as AutobuildRedirect)
      : null;
  const slack =
    o.slack && typeof o.slack === 'object' && typeof (o.slack as AutobuildSlackThread).ts === 'string' && (o.slack as AutobuildSlackThread).ts
      ? (o.slack as AutobuildSlackThread)
      : null;
  return {
    ...(o as AutobuildState),
    phase: str(o.phase) ?? undefined,
    phaseAt: str(o.phaseAt),
    log,
    evidence,
    docs,
    pipeline,
    brief: readAutobuildBrief(o.brief),
    riskReasons,
    decision: d && AUTOBUILD_VERDICTS.includes(d.verdict) ? d : null,
    redirect,
    scope: str(o.scope),
    thread,
    slack,
  };
}

/** Firestore keeps a doc under 1 MiB; five reports plus screenshots must fit with room to spare. */
export const AUTOBUILD_DOC_MAX_CHARS = 60_000;

/** Head of a long document, with a marker, so a 400 KB test report can't sink the write. */
export function capDocText(text: string, max: number = AUTOBUILD_DOC_MAX_CHARS): { text: string; truncated: boolean } {
  const t = String(text ?? '');
  if (t.length <= max) return { text: t, truncated: false };
  return { text: `${t.slice(0, max)}\n\n…(truncated — the full file is on the branch)`, truncated: true };
}

export type AutobuildDocPhase = 'understand' | 'implement' | 'test' | 'regression' | 'audit' | 'walkthrough';

export interface AutobuildDocsInput {
  phase: AutobuildDocPhase;
  at?: string;
  runUrl?: string | null;
  spec?: string | null;
  pr?: string | null;
  changedFiles?: string[] | null;
  test?: string | null;
  testResult?: string | null;
  regression?: string | null;
  regressionResult?: string | null;
  audit?: string | null;
  auditResult?: string | null;
  auditFindings?: unknown[] | null;
  /** The auditor's Brief (audit.json → `brief`), written at the end of the audit. */
  brief?: unknown;
  walkthrough?: string | null;
  walkthroughResult?: string | null;
  walkthroughPreviewUrl?: string | null;
}

function finding(raw: unknown): AutobuildAuditFinding | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const what = typeof r.what === 'string' ? r.what.trim() : '';
  if (!what) return null;
  return {
    severity: typeof r.severity === 'string' && r.severity ? r.severity : 'should',
    ...(typeof r.where === 'string' && r.where ? { where: r.where } : {}),
    what,
    ...(typeof r.why === 'string' && r.why ? { why: r.why } : {}),
    ...(typeof r.how === 'string' && r.how ? { how: r.how } : {}),
  };
}

/**
 * The dotted-path update one phase writes under `autobuild.docs` — pure, so
 * the write-back seam just applies it. Only the keys the phase supplies are
 * touched; an earlier phase's doc is never blanked by a later one.
 */
export function buildAutobuildDocsUpdate(input: AutobuildDocsInput): Record<string, unknown> {
  const at = input.at ?? new Date().toISOString();
  const update: Record<string, unknown> = { 'autobuild.updatedAt': at };
  const put = (key: 'spec' | 'pr' | 'test' | 'regression' | 'audit' | 'walkthrough', text: string | null | undefined) => {
    if (typeof text !== 'string' || !text.trim()) return;
    const capped = capDocText(text);
    const doc: AutobuildDoc = { text: capped.text, at, runUrl: input.runUrl ?? null, ...(capped.truncated ? { truncated: true } : {}) };
    update[`autobuild.docs.${key}`] = doc;
  };
  put('spec', input.spec);
  put('pr', input.pr);
  put('test', input.test);
  put('regression', input.regression);
  put('audit', input.audit);
  put('walkthrough', input.walkthrough);
  if (Array.isArray(input.changedFiles)) {
    update['autobuild.docs.changedFiles'] = input.changedFiles
      .filter((f): f is string => typeof f === 'string' && !!f.trim())
      .map((f) => f.trim())
      .slice(0, 500);
  }
  if (typeof input.testResult === 'string' && input.testResult) update['autobuild.docs.testResult'] = input.testResult;
  if (typeof input.regressionResult === 'string' && input.regressionResult) update['autobuild.docs.regressionResult'] = input.regressionResult;
  if (typeof input.auditResult === 'string' && input.auditResult) update['autobuild.docs.auditResult'] = input.auditResult;
  if (typeof input.walkthroughResult === 'string' && input.walkthroughResult) update['autobuild.docs.walkthroughResult'] = input.walkthroughResult;
  if (typeof input.walkthroughPreviewUrl === 'string' && input.walkthroughPreviewUrl) update['autobuild.docs.walkthroughPreviewUrl'] = input.walkthroughPreviewUrl;
  if (Array.isArray(input.auditFindings)) {
    update['autobuild.docs.auditFindings'] = input.auditFindings.map(finding).filter((f): f is AutobuildAuditFinding => f !== null).slice(0, 100);
  }
  if (input.brief !== undefined) {
    const brief = readAutobuildBrief(input.brief, at, input.runUrl ?? null);
    if (brief) update['autobuild.brief'] = brief;
  }
  return update;
}

export function isAutobuildPhase(v: unknown): v is AutobuildPhase {
  return typeof v === 'string' && (AUTOBUILD_PHASES as readonly string[]).includes(v);
}

/** A question is open until an answer newer than it lands. */
export function hasOpenQuestion(ab: AutobuildState): boolean {
  if (!ab.question) return false;
  if (!ab.answeredAt) return true;
  return (ab.answeredAt ?? '') < (ab.questionAt ?? '');
}

/**
 * The two parked-for-a-person phases a decision applies to: the normal ask
 * (awaitingApproval) and the protected-area stop under `policy.approval =
 * never` (awaitingReuben). Both take the same Approve / Request changes —
 * in Theo, on the Plate, from Claude, or from the Slack buttons — so a
 * protected-area PR is never merged by hand on GitHub.
 */
export const AWAITING_DECISION_PHASES: readonly string[] = ['awaitingApproval', 'awaitingReuben'];

export function isParkedForDecision(phase: string | undefined): boolean {
  return !!phase && AWAITING_DECISION_PHASES.includes(phase);
}

/**
 * The approve / request-changes buttons show while the pipeline is parked
 * for a decision and no decision newer than that ask has been made.
 */
export function isAwaitingDecision(ab: AutobuildState): boolean {
  if (!isParkedForDecision(ab.phase)) return false;
  if (!ab.decision) return true;
  return (ab.decision.at ?? '') < (ab.approvalRequestedAt ?? '');
}

export type AutobuildDispatchKind = 'lodged' | 'answered' | 'approve' | 'changes' | 'redirect';

export interface PendingDispatch {
  kind: AutobuildDispatchKind;
  /** The note that rides along (the reporter's answer or the approver's change request). */
  note?: string;
}

/**
 * What the 5-minute scheduler should do for one report, if anything. Pure:
 * the scheduler reads the doc, calls this, dispatches, stamps the doc.
 *
 *   redirect — a director's Redirect newer than the last redirect dispatch
 *              (any status; the run in flight is cancelled, the branch kept,
 *              and a fresh understand phase starts from the note);
 *   lodged   — a report in a pickable status the scheduler never sent, lodged
 *              on/after `pickableFrom` (the backlog waits for triage);
 *   answered — a report parked with a question whose answer is newer than
 *              the last dispatch and that is back on a pickable status;
 *   approve / changes — a decision newer than the last decision dispatch.
 */
export function pendingDispatch(
  report: { status: string; createdAt: string | null },
  ab: AutobuildState,
  opts: { pickable: readonly string[]; pickableFrom: string | null },
): PendingDispatch | null {
  // A Redirect newer than the last redirect dispatch beats everything: the
  // director has said the build is heading the wrong way. It arrives on
  // whatever status the report is on (building / in_progress included). A
  // redirect a run has already picked up (pickedAt newer — a hand-run from the
  // Actions tab never stamps redirectDispatchedAt) is not sent again.
  if (
    ab.redirect &&
    (ab.redirect.at ?? '') > (ab.redirectDispatchedAt ?? '') &&
    (ab.redirect.at ?? '') > (ab.pickedAt ?? '')
  ) {
    return { kind: 'redirect', note: ab.redirect.note };
  }

  const decision = ab.decision;
  if (
    isParkedForDecision(ab.phase) &&
    decision &&
    (decision.at ?? '') > (ab.decisionDispatchedAt ?? '') &&
    (decision.at ?? '') >= (ab.approvalRequestedAt ?? '')
  ) {
    return { kind: decision.verdict === 'approve' ? 'approve' : 'changes', note: decision.note };
  }

  if (!opts.pickable.includes(report.status)) return null;

  if (ab.question && ab.answeredAt && (ab.answeredAt ?? '') > (ab.dispatchedAt ?? '')) {
    return { kind: 'answered', note: ab.answer ?? '' };
  }

  if (ab.dispatchedAt || ab.pickedAt || ab.phase === 'needsInfo') return null;
  if (opts.pickableFrom && (!report.createdAt || report.createdAt < opts.pickableFrom)) return null;
  return { kind: 'lodged' };
}

export interface BuildHoldInput {
  /** Reports at autobuild.phase = shipping (after the watchdog parked any stale one). */
  shipping: readonly string[];
  /** Something for those ships is queued or running on GitHub: a prepare, the PR's checks, a finish. */
  shipMoving: boolean;
  /** Reports whose Approve the dispatcher has not sent yet (pendingDispatch kind 'approve'). */
  approvalsWaiting: readonly string[];
}

export interface BuildHold {
  hold: boolean;
  /** One line for the dispatcher's log and the drain step's output. */
  reason: string;
}

/**
 * Ships before builds. Builds, a ship's prepare and every PR's `gates` share
 * one heavy runner, oldest job first, and a build holds it for hours: a build
 * that starts while a ship is waiting or under way puts that ship — or the
 * next approved one — behind a whole build. So no NEW build starts while a
 * ship is under way or an Approve waits to be sent; a build already running is
 * never stopped, so a ship waits for at most that one build.
 *
 * A report `shipping` with nothing moving for it on GitHub is stuck. It holds
 * no builds (its watchdog parks it), and neither does an Approve waiting behind
 * it, which cannot start before the watchdog frees the ship slot anyway.
 */
export function buildHold(input: BuildHoldInput): BuildHold {
  if (input.shipping.length > 0) {
    const ids = input.shipping.join(', ');
    return input.shipMoving
      ? { hold: true, reason: `Ship under way (${ids}) — the next build starts when it ends.` }
      : { hold: false, reason: `${ids} is shipping but nothing is running for it — builds are not held for a stuck ship (the watchdog parks it).` };
  }
  if (input.approvalsWaiting.length > 0) {
    return { hold: true, reason: `Approved ship about to start (${input.approvalsWaiting.join(', ')}) — it goes before the next build.` };
  }
  return { hold: false, reason: 'No ship waiting or under way.' };
}

export interface AutobuildWrite {
  /** Dotted-path field updates for a Firestore update(). */
  fields: Record<string, unknown>;
  /** Appended to `autobuild.log` by the caller with its SDK's arrayUnion. */
  logEntry: AutobuildLogEntry;
}

export function autobuildLogEntry(
  phase: string,
  text: string,
  opts: { at?: string; by?: string; runUrl?: string | null } = {},
): AutobuildLogEntry {
  return {
    at: opts.at ?? new Date().toISOString(),
    phase,
    text,
    ...(opts.runUrl !== undefined ? { runUrl: opts.runUrl } : {}),
    ...(opts.by ? { by: opts.by } : {}),
  };
}

/**
 * The reporter (or a director on their behalf) answering the pipeline's
 * question. The caller also sets the app status back to 'new' — that reopen,
 * plus `answeredAt`, is what the scheduler resumes on.
 */
export function autobuildAnswerWrite(input: {
  answer: string;
  byId: string;
  byName: string;
  at?: string;
}): AutobuildWrite {
  const at = input.at ?? new Date().toISOString();
  const answer = input.answer.trim();
  return {
    fields: {
      'autobuild.answer': answer,
      'autobuild.answeredAt': at,
      'autobuild.answeredBy': input.byId,
      'autobuild.answeredByName': input.byName,
      'autobuild.updatedAt': at,
    },
    logEntry: autobuildLogEntry('answered', answer, { at, by: input.byName }),
  };
}

export function validateDecision(
  verdict: unknown,
  note: unknown,
): { ok: true; verdict: AutobuildVerdict; note: string } | { ok: false; reason: string } {
  if (!AUTOBUILD_VERDICTS.includes(verdict as AutobuildVerdict)) {
    return { ok: false, reason: "verdict must be 'approve' or 'changes'." };
  }
  const trimmed = typeof note === 'string' ? note.trim() : '';
  if (verdict === 'changes' && !trimmed) {
    return { ok: false, reason: 'Say what to change — the fix loop builds on your words.' };
  }
  if (trimmed.length > 4000) {
    return { ok: false, reason: 'Keep the note under 4000 characters.' };
  }
  return { ok: true, verdict: verdict as AutobuildVerdict, note: trimmed };
}

/** A director's Approve / Request changes. Server-written only (callable or connector). */
export function autobuildDecisionWrite(input: {
  verdict: AutobuildVerdict;
  note: string;
  byId: string;
  byName: string;
  via: AutobuildSource;
  at?: string;
}): AutobuildWrite {
  const at = input.at ?? new Date().toISOString();
  const decision: AutobuildDecision = {
    verdict: input.verdict,
    note: input.note.trim(),
    at,
    byId: input.byId,
    byName: input.byName,
    via: input.via,
  };
  const text =
    input.verdict === 'approve'
      ? `Approved by ${input.byName}${decision.note ? ` — ${decision.note}` : ''}`
      : `Changes requested by ${input.byName}: ${decision.note}`;
  return {
    fields: {
      'autobuild.decision': decision,
      'autobuild.updatedAt': at,
    },
    logEntry: autobuildLogEntry(input.verdict === 'approve' ? 'approved' : 'changesRequested', text, {
      at,
      by: input.byName,
    }),
  };
}

// ─── Redirect ──────────────────────────────────────────────────────────────

/**
 * The phases a Redirect applies to: anything from understanding to awaiting
 * approval (both parked-for-decision stops), plus a build parked as blocked
 * — "you're building the wrong thing" is the usual reason it blocked. Not
 * shipping / shipped / already done / declined: nothing to redirect.
 */
export const REDIRECTABLE_PHASES: readonly string[] = [
  'queued',
  'understanding',
  'needsInfo',
  'answered',
  'building',
  'testing',
  'awaitingApproval',
  'awaitingReuben',
  'blocked',
  'redirected',
];

export function canRedirect(ab: AutobuildState): boolean {
  return !!ab.phase && REDIRECTABLE_PHASES.includes(ab.phase);
}

export const AUTOBUILD_REDIRECT_MAX_CHARS = 4000;

export function validateRedirect(note: unknown): { ok: true; note: string } | { ok: false; reason: string } {
  const trimmed = typeof note === 'string' ? note.trim() : '';
  if (!trimmed) return { ok: false, reason: 'Say what to build instead — the new spec starts from your words.' };
  if (trimmed.length > AUTOBUILD_REDIRECT_MAX_CHARS) return { ok: false, reason: `Keep the note under ${AUTOBUILD_REDIRECT_MAX_CHARS} characters.` };
  return { ok: true, note: trimmed };
}

/**
 * A director's Redirect. Server-written only. Moves the report to
 * `redirected`, clears any open question (the note supersedes it), and logs
 * "Redirected by <name>: <note>" — the line every phase repeats on the
 * timeline when it restarts. The app status is left to the pipeline (the
 * fresh understand phase sets it, like any pick-up).
 */
export function autobuildRedirectWrite(input: {
  note: string;
  byId: string;
  byName: string;
  via: AutobuildSource;
  phaseBefore?: string | null;
  stoppedRunUrl?: string | null;
  at?: string;
}): AutobuildWrite & { redirect: AutobuildRedirect; post: AutobuildThreadPost } {
  const at = input.at ?? new Date().toISOString();
  const redirect: AutobuildRedirect = {
    note: input.note.trim(),
    at,
    byId: input.byId,
    byName: input.byName,
    via: input.via,
    phaseBefore: input.phaseBefore ?? null,
    stoppedRunUrl: input.stoppedRunUrl ?? null,
  };
  const text = `Redirected by ${input.byName}: ${redirect.note}`;
  return {
    fields: {
      'autobuild.redirect': redirect,
      'autobuild.phase': 'redirected',
      'autobuild.phaseAt': at,
      'autobuild.question': null,
      'autobuild.questionAt': null,
      'autobuild.lastNote': text,
      'autobuild.updatedAt': at,
    },
    logEntry: autobuildLogEntry('redirected', text, { at, by: input.byName }),
    redirect,
    post: threadPost({ byId: input.byId, byName: input.byName, text: redirect.note, kind: 'redirect', via: input.via, at }),
  };
}

// ─── The thread ────────────────────────────────────────────────────────────

export const AUTOBUILD_THREAD_POST_MAX_CHARS = 4000;

/** A thread post with a unique id (time + a short random tail). */
export function threadPost(input: {
  byId: string;
  byName: string;
  text: string;
  kind: AutobuildThreadKind;
  via: AutobuildSource;
  at?: string;
  phase?: string | null;
  runUrl?: string | null;
  slackTs?: string | null;
  id?: string;
}): AutobuildThreadPost {
  const at = input.at ?? new Date().toISOString();
  const tail = Math.random().toString(36).slice(2, 8);
  return {
    id: input.id ?? `${at}-${tail}`,
    at,
    byId: input.byId,
    byName: input.byName,
    text: input.text.trim().slice(0, AUTOBUILD_THREAD_POST_MAX_CHARS),
    kind: input.kind,
    via: input.via,
    ...(input.phase !== undefined ? { phase: input.phase } : {}),
    ...(input.runUrl !== undefined ? { runUrl: input.runUrl } : {}),
    ...(input.slackTs !== undefined ? { slackTs: input.slackTs } : {}),
  };
}

/** One item of the thread as a person (or a phase) reads it. */
export interface ThreadItem {
  id: string;
  at: string;
  byId: string;
  byName: string;
  text: string;
  kind: AutobuildThreadKind | 'status';
  via: AutobuildSource;
  phase?: string | null;
  runUrl?: string | null;
  /** The pipeline wrote it (a phase post or a status line). */
  pipeline: boolean;
  /** A tenant owner wrote it — the pipeline treats it as an instruction. */
  instruction: boolean;
  /** A person's post the pipeline has not replied to yet (newer than its last post). */
  unanswered: boolean;
}

function lower(v: unknown): string {
  return typeof v === 'string' ? v.toLowerCase() : '';
}

/**
 * The thread as shown and as every phase reads it: the people's posts and
 * the phases' replies (`autobuild.thread`) merged with the pipeline's status
 * lines (`autobuild.log`), oldest first. A status line that mirrors a thread
 * post (same instant, same author) shows once. Owner-ness comes from
 * `ownerIds` at read time; `unanswered` marks people's posts newer than the
 * pipeline's last word — the understand phase answers those before it specs.
 */
export function threadView(ab: AutobuildState, ownerIds: readonly string[] = []): ThreadItem[] {
  const owners = new Set(ownerIds.map(lower).filter(Boolean));
  const posts = ab.thread ?? [];
  const seen = new Set(posts.map((p) => `${p.at}|${lower(p.byName)}`));
  const items: ThreadItem[] = posts.map((p) => {
    const pipeline = p.byId === AUTOBUILD_ACTOR.id || p.via === 'pipeline';
    return {
      id: p.id,
      at: p.at,
      byId: p.byId,
      byName: p.byName,
      text: p.text,
      kind: p.kind,
      via: p.via,
      phase: p.phase ?? null,
      runUrl: p.runUrl ?? null,
      pipeline,
      instruction: !pipeline && owners.has(lower(p.byId)),
      unanswered: false,
    };
  });
  for (const [i, e] of (ab.log ?? []).entries()) {
    if (!e || !e.at) continue;
    const by = e.by ? lower(e.by) : '';
    if (by && seen.has(`${e.at}|${by}`)) continue;
    const pipeline = !e.by;
    items.push({
      id: `log-${e.at}-${i}`,
      at: e.at,
      byId: pipeline ? AUTOBUILD_ACTOR.id : '',
      byName: e.by ?? AUTOBUILD_ACTOR.name,
      text: e.text,
      kind: 'status',
      via: 'pipeline',
      phase: e.phase,
      runUrl: e.runUrl ?? null,
      pipeline,
      instruction: false,
      unanswered: false,
    });
  }
  items.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : a.id.localeCompare(b.id)));
  // People's posts after the pipeline's last word are waiting on a reply.
  let lastPipelineAt = '';
  for (const it of items) if (it.pipeline && it.at > lastPipelineAt) lastPipelineAt = it.at;
  for (const it of items) it.unanswered = !it.pipeline && it.kind === 'post' && it.at > lastPipelineAt;
  return items;
}

/** The write for a person's (or a phase's) post: the post itself, appended by the caller with arrayUnion. */
export function autobuildThreadWrite(post: AutobuildThreadPost): { fields: Record<string, unknown>; post: AutobuildThreadPost } {
  return { fields: { 'autobuild.updatedAt': post.at }, post };
}

export interface PhaseDisplay {
  label: string;
  tone: 'wait' | 'busy' | 'ok' | 'bad' | 'muted';
  /** True while someone has to do something for the build to continue. */
  needsHuman: boolean;
}

/** How the timeline and the connector describe a phase to a person. */
export function describeAutobuildPhase(phase: string | undefined): PhaseDisplay {
  switch (phase) {
    case 'lodged':
    case 'queued':
      return { label: 'Queued for autobuild', tone: 'muted', needsHuman: false };
    case 'understanding':
      return { label: 'Working out the spec', tone: 'busy', needsHuman: false };
    case 'needsInfo':
      return { label: 'Waiting on an answer', tone: 'wait', needsHuman: true };
    case 'answered':
      return { label: 'Answer received', tone: 'busy', needsHuman: false };
    case 'building':
      return { label: 'Building', tone: 'busy', needsHuman: false };
    case 'testing':
      return { label: 'Testing', tone: 'busy', needsHuman: false };
    case 'awaitingApproval':
      return { label: 'Ready — waiting for approval', tone: 'wait', needsHuman: true };
    case 'awaitingReuben':
      return { label: 'Needs sign-off (protected area)', tone: 'wait', needsHuman: true };
    case 'redirected':
      return { label: 'Redirected — starting again from the note', tone: 'busy', needsHuman: false };
    case 'shipping':
      return { label: 'Approved — shipping', tone: 'busy', needsHuman: false };
    case 'blocked':
      return { label: 'Blocked — needs a human', tone: 'bad', needsHuman: true };
    case 'rolledBack':
      return { label: 'Rolled back', tone: 'bad', needsHuman: true };
    case 'shipped':
      return { label: 'Shipped', tone: 'ok', needsHuman: false };
    case 'alreadyDone':
      return { label: 'Already in Theo', tone: 'ok', needsHuman: false };
    case 'declined':
      return { label: 'Declined', tone: 'muted', needsHuman: false };
    default:
      return { label: phase ? phase : 'Not picked up', tone: 'muted', needsHuman: false };
  }
}

/** Evidence still worth showing (signed URLs expire after 7 days). */
export function liveEvidence(ab: AutobuildState, now: string = new Date().toISOString()): AutobuildEvidence[] {
  return (ab.evidence ?? []).filter((e) => !e.expiresAt || e.expiresAt > now);
}
